import { useEffect, useState } from 'react';

import { ApiError, api } from './api/client';
import { ChartRenderer } from './components/ChartRenderer';
import { ChartStudio } from './components/ChartStudio';
import { DashboardBuilder } from './components/DashboardBuilder';
import { SignedOut } from './components/SignedOut';
import { WidgetBuilder } from './components/WidgetBuilder';
import { embedParams } from './embedParams';
import { ChartView } from './routes/ChartView';
import { DashboardView, WidgetView } from './routes/DashboardView';
import { Gallery } from './routes/Gallery';
import { Help } from './components/Help';
import { TestDialog, type TestTarget } from './components/TestDialog';
import type { CatalogChart, SavedChart, SavedDashboard, SavedTemplate, SavedWidget } from './types';

/**
 * Two things live here: the embed routes the platform points at (`/d/…`,
 * `/w/…`), and the authoring UI everything else falls through to.
 *
 * Routing is a path match rather than a router dependency — the app has three
 * shapes, and a library would be more moving parts than the problem needs.
 */

type Tab = 'charts' | 'dashboards' | 'widgets' | 'templates' | 'help';
type Editor =
  | { kind: 'chart'; value: SavedChart | null; seed?: { html: string; name?: string; description?: string } }
  | { kind: 'dashboard'; value: SavedDashboard | null }
  | { kind: 'widget'; value: SavedWidget | null }
  | null;

const Authoring = () => {
  const [tab, setTab] = useState<Tab>('charts');
  const [charts, setCharts] = useState<SavedChart[]>([]);
  const [dashboards, setDashboards] = useState<SavedDashboard[]>([]);
  const [widgets, setWidgets] = useState<SavedWidget[]>([]);
  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  const [editor, setEditor] = useState<Editor>(null);
  const [error, setError] = useState<string | null>(null);
  /** No session is a normal state to arrive in, not a failure. */
  const [signedOut, setSignedOut] = useState(false);
  /** The backend catalogue, for knowing an item's required parameters at test time. */
  const [catalog, setCatalog] = useState<CatalogChart[]>([]);
  /** The item currently being tested, or null. */
  const [testing, setTesting] = useState<TestTarget | null>(null);

  /** App filter for the card previews — see the ChartRenderer call below. */
  const viewParams = embedParams();

  const refresh = () => {
    Promise.all([api.charts(), api.dashboards(), api.widgets(), api.templates()])
      .then(([c, d, w, t]) => {
        setCharts(c);
        setDashboards(d);
        setWidgets(w);
        setTemplates(t);
        setSignedOut(false);
        setError(null);
        // The catalogue says which parameters each query requires — needed by the
        // Test button. Load it for every source the saved charts actually use.
        const sources = [...new Set(c.map((x) => x.source))];
        Promise.all(sources.map((src) => api.catalog(src).catch(() => [])))
          .then((lists) => setCatalog(lists.flat()));
      })
      .catch((err: Error) => {
        // 401 means "you have not signed in", which deserves an explanation and
        // a way in — not the API's own wording printed over an empty page.
        if (err instanceof ApiError && err.status === 401) setSignedOut(true);
        else setError(err.message);
      });
  };

  useEffect(refresh, []);

  const closeEditor = () => {
    setEditor(null);
    refresh();
  };

  if (editor?.kind === 'chart') {
    return (
      <ChartStudio
        editing={editor.value}
        seed={editor.seed ?? null}
        onSaved={closeEditor}
        onCancel={() => setEditor(null)}
      />
    );
  }
  if (editor?.kind === 'dashboard') {
    return (
      <DashboardBuilder
        charts={charts}
        editing={editor.value}
        onSaved={closeEditor}
        onCancel={() => setEditor(null)}
      />
    );
  }
  if (editor?.kind === 'widget') {
    return (
      <WidgetBuilder
        charts={charts}
        editing={editor.value}
        onSaved={closeEditor}
        onCancel={() => setEditor(null)}
      />
    );
  }

  if (signedOut) {
    return (
      <div className="app">
        <header className="app__header">
          <h1>appLockr Reports</h1>
        </header>
        <SignedOut />
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app__header">
        <h1>appLockr Reports</h1>
        <nav>
          {(['charts', 'dashboards', 'widgets', 'templates', 'help'] as Tab[]).map((t) => (
            <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
      </header>

      {error && <p className="error">{error}</p>}

      {tab === 'charts' && (
        <section>
          <div className="section-head">
            <h2>Charts</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'chart', value: null })}>
              New chart
            </button>
          </div>
          {charts.length === 0 && <p className="hint">No charts yet.</p>}
          <div className="card-grid">
            {charts.map((c) => (
              <article className="card" key={c.id}>
                <header>
                  <strong>{c.name}</strong>
                  <span className="badge">{c.kind === 'custom' ? 'AI' : c.template}</span>
                </header>
                <div className="card__body">
                  {/* Saved charts store no app filter — the studio deliberately
                      keeps preview params out of the saved chart, because at
                      view time the platform supplies them. So the app filter has
                      to come from the URL here exactly as it does on /d/<slug>
                      and /w/<id>: embedParams() forwards a real selection when
                      there is one, and falls back to DEFAULT_APP_ID outside a
                      platform embed, which the library always is.

                      Without it these cards queried with no app_id at all, and
                      every chart the catalogue marks required_params:["app_id"]
                      — 1, 2, 6, 14, 57 — came back "HTTP 400: JWT does not
                      contain required params: app_id" the moment it was saved,
                      having rendered perfectly in Test one click earlier. */}
                  <ChartRenderer chart={c} overrideParams={viewParams} chrome={false} />
                </div>
                <footer>
                  <button onClick={() => setTesting({ kind: 'chart', chart: c })}>Test</button>
                  <button onClick={() => setEditor({ kind: 'chart', value: c })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteChart(c.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </footer>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === 'dashboards' && (
        <section>
          <div className="section-head">
            <h2>Dashboards</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'dashboard', value: null })}>
              New dashboard
            </button>
          </div>
          {dashboards.length === 0 && <p className="hint">No dashboards yet.</p>}
          <ul className="list">
            {dashboards.map((d) => (
              <li key={d.id}>
                <div>
                  <strong>{d.name}</strong>
                  <code>/d/{d.slug}</code>
                  <span className="hint">
                    {d.tiles.length} tile{d.tiles.length === 1 ? '' : 's'} · filter: {d.appFilter}
                  </span>
                </div>
                <div>
                  <a href={`/d/${d.slug}`} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button onClick={() => setTesting({ kind: 'dashboard', dashboard: d })}>Test</button>
                  <button onClick={() => setEditor({ kind: 'dashboard', value: d })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteDashboard(d.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'widgets' && (
        <section>
          <div className="section-head">
            <h2>Widgets</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'widget', value: null })}>
              New widget
            </button>
          </div>
          {widgets.length === 0 && <p className="hint">No widgets yet.</p>}
          <ul className="list">
            {widgets.map((w) => (
              <li key={w.id}>
                <div>
                  <strong>{w.name}</strong>
                  <code>/w/{w.id}</code>
                  <span className="hint">{w.aspectRatio}</span>
                </div>
                <div>
                  <a href={`/w/${w.id}`} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button onClick={() => setTesting({ kind: 'widget', widget: w })}>Test</button>
                  <button onClick={() => setEditor({ kind: 'widget', value: w })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteWidget(w.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'templates' && (
        <section>
          <div className="section-head">
            <h2>Templates</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'chart', value: null })}>
              New chart
            </button>
          </div>
          <p className="hint">
            Reusable markup you've saved from a built chart (in the studio's Build-with-AI tab, use
            “Save as template”). Start a new chart from one here, or pick it in any studio.
          </p>
          {templates.length === 0 && (
            <p className="hint">
              No templates yet. Build a custom chart, then choose “Save as template” to reuse it.
            </p>
          )}
          <ul className="list">
            {templates.map((t) => (
              <li key={t.id}>
                <div>
                  <strong>{t.name}</strong>
                  <span className="hint">
                    {t.description ?? 'Reusable HTML template'} · saved{' '}
                    {new Date(t.createdAt).toLocaleDateString('en-GB')}
                  </span>
                </div>
                <div>
                  <button
                    onClick={() =>
                      setEditor({
                        kind: 'chart',
                        value: null,
                        seed: { html: t.html, name: t.name, description: t.description },
                      })
                    }
                  >
                    New chart from this
                  </button>
                  <button
                    onClick={async () => {
                      await api.deleteTemplate(t.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'help' && (
        <section>
          <div className="section-head">
            <h2>Help</h2>
          </div>
          <Help />
        </section>
      )}

      {testing && (
        <TestDialog
          target={testing}
          charts={charts}
          catalog={catalog}
          onClose={() => setTesting(null)}
        />
      )}
    </div>
  );
};

export const App = () => {
  const path = window.location.pathname;

  const dashboard = path.match(/^\/d\/([a-z0-9-]+)\/?$/);
  if (dashboard) return <DashboardView slug={dashboard[1]} />;

  const widget = path.match(/^\/w\/([A-Za-z0-9_-]+)\/?$/);
  if (widget) return <WidgetView widgetId={widget[1]} />;

  // One catalogue query, drawn straight from the URL — nothing saved first.
  const chart = path.match(/^\/c\/([A-Za-z0-9_-]+)\/?$/);
  if (chart) return <ChartView chartId={chart[1]} />;

  // The chart library rendered against fixtures — no backend, no session.
  if (/^\/preview\/?$/.test(path)) return <Gallery />;

  // The help section, readable without a session and linkable on its own.
  if (/^\/help\/?$/.test(path)) {
    return (
      <div className="app">
        <header className="app__header">
          <h1>appLockr Reports</h1>
        </header>
        <section>
          <div className="section-head">
            <h2>Help</h2>
          </div>
          <Help />
        </section>
      </div>
    );
  }

  return <Authoring />;
};
